import { c as _c } from "react/compiler-runtime";
import { Group } from "lucide-react";
import useRealEstateDetail from "@/hooks/QueryHooks/useRealEstateDetail";
import { useDialogDetailsActions, useDialogDetailsPersistedState } from "../dialogComponents/DialogProvider";
import { usePropertyDetailSwitcher } from "../hooks/usePropertyDetailSwitcher";
import { TitleSwitcher } from "@/components/atoms/TitleSwitcher/TitleSwitcher";
import { RealEstateDetailMobileScaffold } from "./RealEstateDetailMobileScaffold";
import RealEstateDetailMobileSkeleton from "./RealEstateDetailMobileSkeleton";
import { RoomSwitchOverlay } from "../components/room-switch-overlay/RoomSwitchOverlay";
import { cn, formatLocationDisplay } from "@/lib/utils";
import { mergeListingImages } from "@/utils/listingImages";
import { useEffect, useMemo, useState } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import { ROUTE_PATHS } from "@/constants/routes";
import { isSearchRoute } from "@/utils/urlManipulation";
import { usePreventSwipeNavigation } from "@/hooks/usePreventSwipe";
import { useView } from "../../../pages/search/providers/ViewProvider";
import { LocationCard } from "@/components/molecules/LocationCard/LocationCard";
import CardFavorite from "@/components/cardElements/FavoritesModalGroup/CardFavorite";
import { ShareDrawer } from "../RealEstateDetail/components/share-drawer";
import { EleiloesSubType } from "@/types/real-estate-features";
import { DetailHeaderBlock, DetailFactsRow, DetailAttributeRows, DetailDescriptionSkeleton } from "./RealEstateDetailMobileSections";
import Price from "@/components/atoms/Price";
import { useDeviceOrientation } from "@/hooks/useDeviceOrientation";
import { useDeferredTransition } from "@/hooks/useDeferredTransition";
import RealEstateAgencyCard from "../components/RealEstateAgencyCard/RealEstateAgencyCard";
import { RealEstateDetailHouseRules } from "../RealEstateDetail/components/RealEstateDetailHouseRules";
import { RealEstateDetailBills } from "../RealEstateDetail/components/RealEstateDetailBills";
import { RoomRail, type RoomRailItem } from "../RealEstateDetail/components/room-rail/RoomRail";
import { useRoomReason } from "../RealEstateDetail/components/room-rail/useRoomRail";
import NotificationMessage from "@/components/atoms/notifications/NotificationMessage";
import { useLocale, useTranslations } from "next-intl";
import { formatTimeAgo, formatSmartAvailability } from "@/lib/time";
import { EnergeticCertificate } from "../RealEstateDetail/components/EnergeticCertificate";
import { useFiltersContext } from "@/components/providers/FiltersProvider/FiltersProvider";
import { ListingNotFound } from "@/components/modules/listings/ListingDetail/listing-not-found/ListingNotFound";
import { useMapContext } from "../../../organisms/map/components/MapProvider";
import { BusinessType } from "@/types/real-estate";
import RealEstateDescriptionText, { hasRenderableDescription } from "../RealEstateDetail/components/RealEstateDescriptionText";
import RealEstateFeaturesCards from "../RealEstateDetail/components/RealEstateFeaturesCards";
import { RoomFeaturePills } from "../RealEstateDetail/components/RoomFeaturePills";
import { getListingFeaturePills } from "@/components/modules/listings/mappers";
import useSession from "@/hooks/QueryHooks/useSession";
import { RealEstateDetailMobileMessageFab } from "./components/real-estate-detail-mobile-message-fab/RealEstateDetailMobileMessageFab";
import { SearchFilters, SearchTypes } from "@/constants/filters";
import { buildRoomFilterParams } from "../roomFilterParams";
import RealEstateDetailImageSectionMobileInDetail from "./RealEstateDetailImageSectionMobileInDetail";
import { useToast } from "@/hooks/use-toast";
import { DemandUserCard } from "../RealEstateDetail/components/demand-user-card/DemandUserCard";
import { Gender, ListingType } from "../../listings/api/schema";
import { useBoundaries } from "@/hooks/useBoundaries";
import { convertPolygon } from "@/components/pages/search/map/components/MapPolygons";
import { selectDemandBoundaryNodes } from "../demandLocation";
import { DemandLocationName } from "../demand-location-name/DemandLocationName";
function CompareGroupButton(t0) {
  const $ = _c(15);
  const {
    realEstateSlug
  } = t0;
  const {
    persistedModals
  } = useDialogDetailsPersistedState();
  const {
    handlePersistDialog,
    handleClosePill
  } = useDialogDetailsActions();
  const {
    toast
  } = useToast();
  let t1;
  if ($[0] !== persistedModals || $[1] !== realEstateSlug) {
    t1 = persistedModals.includes(realEstateSlug);
    $[0] = persistedModals;
    $[1] = realEstateSlug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isPersisted = t1;
  const tRealEstateDetail = useTranslations("modules.realEstateDetail");
  const t2 = isPersisted ? "text-foreground" : "text-faint-foreground";
  let t3;
  if ($[3] !== t2) {
    t3 = cn("", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== handleClosePill || $[6] !== handlePersistDialog || $[7] !== isPersisted || $[8] !== realEstateSlug || $[9] !== tRealEstateDetail || $[10] !== toast) {
    t4 = () => {
      if (isPersisted) {
        handleClosePill(realEstateSlug);
        toast({
          title: tRealEstateDetail("removedFromCompare")
        });
        return;
      }
      handlePersistDialog(realEstateSlug);
      toast({
        title: tRealEstateDetail("addedToCompare")
      });
    };
    $[5] = handleClosePill;
    $[6] = handlePersistDialog;
    $[7] = isPersisted;
    $[8] = realEstateSlug;
    $[9] = tRealEstateDetail;
    $[10] = toast;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3 || $[13] !== t4) {
    t5 = <Group className={t3} onClick={t4} data-sentry-element="Group" data-sentry-component="CompareGroupButton" data-sentry-source-file="RealEstateDetailMobile.tsx" />;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
interface PropertyListingProps {
  realEstateSlug: string;
  inDialog?: boolean;
  onRoomChange?: (slug: string) => void;
}
export default function RealEstateDetailMobile({
  realEstateSlug,
  inDialog,
  onRoomChange
}: PropertyListingProps) {
  const {
    handleCloseDialog,
    handleOpenDialog
  } = useDialogDetailsActions();
  const {
    items: switcherItems,
    currentId: switcherCurrentId
  } = usePropertyDetailSwitcher(realEstateSlug);
  const tRealEstateDetail = useTranslations("modules.realEstateDetail");
  const tPills = useTranslations("components.realEstatePills");
  const {
    data: sessionData
  } = useSession();
  const {
    handleGoToSearch,
    params: filterParams,
    businessType: searchBusinessType
  } = useFiltersContext();
  const params = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInModal = params.districtParam === undefined;
  const isInSearch = isSearchRoute(pathname);
  const orientation = useDeviceOrientation();
  usePreventSwipeNavigation(isInModal);
  const router = useRouter();
  const [heroCurrent, setHeroCurrent] = useState(1);
  const {
    showMap,
    handleShowMap
  } = useView();
  const {
    setForcedMapTarget
  } = useMapContext();
  const detailFilters = searchBusinessType === SearchTypes.RENT_ROOM ? buildRoomFilterParams(filterParams) : undefined;
  const realEstateDetail = useRealEstateDetail({
    realEstateSlug,
    filters: detailFilters
  });
  const roomReason = useRoomReason(detailFilters);
  const tLandingPage = useTranslations("modules.landingPage");
  const locale = useLocale();
  const detailData = realEstateDetail.data?.data.realEstate;
  const pictures = mergeListingImages(detailData?.pictures, detailData?.portfolioData?.imageUrls);
  const isFavorite = realEstateDetail.data?.data.isFavorite;
  const locations = realEstateDetail.data?.data.locations;
  const featuresData = realEstateDetail.data?.data.features;
  const featurePills = getListingFeaturePills(featuresData);
  const analytics = realEstateDetail.data?.data.analytics;
  const user = realEstateDetail.data?.data.user;
  const realEstateId = featuresData?.find(feature => feature.featureKey === "real_estate_type")?.featureValue;
  const realEstateType = EleiloesSubType[realEstateId];
  const isRentLike = (type?: BusinessType) => type === BusinessType.Rent || type === BusinessType.RoomRent;
  const rentLike = isRentLike(detailData?.businessType);

  // Demand reuses this detail: the images hero becomes an avatar/bio card and
  // the location is an area polygon instead of a point.
  const isDemand = detailData?.listingType === ListingType.DEMAND;
  const geometryByCode = useBoundaries(isDemand ? selectDemandBoundaryNodes(locations) : []);
  const locationPolygons = [...geometryByCode.values()].map(geometry => convertPolygon(geometry)).filter(points => points.length > 0);
  const isLoading = realEstateDetail.isLoading;
  const {
    isOverlayVisible: isSwitchingRoom
  } = useDeferredTransition({
    isTransitioning: realEstateDetail.isPlaceholderData,
    data: null,
    minVisibleMs: 400
  });
  const isOwner = useMemo(() => user?.id === sessionData?.id, [user, sessionData]);
  useEffect(() => {
    const onPopState = () => {
      handleGoBack();
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData?.slug, detailData?.id, isInModal]);
  const locationDisplay = formatLocationDisplay(locations);
  const handleNavigateInMap = () => {
    if (!showMap) handleShowMap();
    setTimeout(() => setForcedMapTarget({
      realEstateId: detailData.id
    }), 1000);
  };
  const handleGoToMap = () => {
    if (isInModal && isInSearch) {
      handleCloseDialog(detailData.id);
      handleNavigateInMap();
    } else {
      handleGoToSearch();
      setTimeout(() => {
        handleNavigateInMap();
      }, 1000);
    }
  };
  const handleGoBack = () => {
    isInModal ? handleCloseDialog(detailData?.id) : router.push(`${ROUTE_PATHS.SEARCH}?${SearchFilters.BUSINESS_TYPE}=${detailData?.businessType}${detailData?.businessType !== BusinessType.RoomRent ? `&${SearchFilters.REAL_ESTATE_TYPE}=${realEstateId}` : ""}`);
  };
  if (realEstateDetail.isError && !realEstateDetail.isLoading) {
    return <div className="py-4 flex items-center justify-center h-full min-h-[90vh]">
        <ListingNotFound />
      </div>;
  }
  if (isLoading) return <RealEstateDetailMobileSkeleton showRail={searchBusinessType === SearchTypes.RENT_ROOM} />;
  if (!detailData || !realEstateSlug) return null;

  // Non-room (sale/rent/auction) attributes as a simple label/value rows list,
  // shown near the top — replaces the old grid of cards + area bars on mobile.
  const attributeRows: {
    label: string;
    value: React.ReactNode;
  }[] = [];
  if (detailData.businessType !== BusinessType.RoomRent) {
    if (realEstateType) attributeRows.push({
      label: tRealEstateDetail("type"),
      value: realEstateType
    });
    if (detailData.energyCertificationType) attributeRows.push({
      label: tRealEstateDetail("energeticCertificate"),
      value: <EnergeticCertificate value={detailData.energyCertificationType} />
    });
    if (detailData.totalArea && detailData.salePrice) attributeRows.push({
      label: tRealEstateDetail("pricePerSqm"),
      value: `${(Number(detailData.salePrice) / Number(detailData.totalArea)).toFixed(1)} ${tRealEstateDetail("euroPricePerSqm")}`
    });
  } else if (detailData.roomData) {
    const room = detailData.roomData;
    attributeRows.push({
      label: tRealEstateDetail("roomType"),
      value: room.maxPersons === 2 ? tRealEstateDetail("doubleRoom") : room.maxPersons && room.maxPersons >= 3 ? tRealEstateDetail("dormitory") : tRealEstateDetail("singleRoom")
    });
    const genderIsAny = room.gender !== Gender.MALE && room.gender !== Gender.FEMALE;
    // A demand with "any" gender carries no info — hide that row.
    if (room.gender && !(isDemand && genderIsAny)) attributeRows.push({
      label: tRealEstateDetail("gender"),
      value: <span className={room.gender === Gender.MALE ? "text-blue-500" : room.gender === Gender.FEMALE ? "text-rose-500" : "text-foreground-heading"}>
            {room.gender === Gender.MALE ? tRealEstateDetail("maleGender") : room.gender === Gender.FEMALE ? tRealEstateDetail("femaleGender") : tRealEstateDetail("anyGender")}
          </span>
    });
    if (room.availableFrom) attributeRows.push({
      label: tRealEstateDetail("availability"),
      value: formatSmartAvailability(room.availableFrom, locale === "pt" ? "pt" : "enGB", true)
    });
    if (detailData.reference) attributeRows.push({
      label: tRealEstateDetail("reference"),
      value: detailData.propertyUrl ? <a href={detailData.propertyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-muted-foreground transition-colors">
            {detailData.reference}
          </a> : detailData.reference
    });
  }
  const relatedListings = realEstateDetail.data?.data?.relatedListings ?? [];
  const roomRailItems: RoomRailItem[] = detailData.businessType === BusinessType.RoomRent && relatedListings.length > 0 ? [...relatedListings.map(r => ({
    slug: r.slug,
    title: r.title,
    rentPrice: r.rentPrice ?? null,
    thumbnail: r.thumbnail ?? null,
    matchesFilters: r.matchesFilters,
    gender: r.gender ?? null,
    availableFrom: r.availableFrom ?? null,
    petFriendly: r.petFriendly ?? null,
    contract: r.contract ?? null,
    smokingAllowed: r.smokingAllowed ?? null
  })), {
    slug: detailData.slug,
    title: detailData.title,
    rentPrice: detailData.rentPrice,
    thumbnail: detailData.pictures?.[0] ?? null,
    matchesFilters: realEstateDetail.data?.data?.matchesFilters,
    gender: detailData.roomData?.gender ?? null,
    availableFrom: detailData.roomData?.availableFrom ?? null,
    petFriendly: detailData.roomData?.petFriendly ?? null,
    contract: detailData.roomData?.contract ?? null,
    smokingAllowed: detailData.roomData?.smokingAllowed ?? null
  }].sort((a, b) => a.title.localeCompare(b.title) || a.slug.localeCompare(b.slug)) : [];
  const currentRoomOutsideReason = detailData.businessType === BusinessType.RoomRent && relatedListings.length > 0 && realEstateDetail.data?.data?.matchesFilters === false ? roomReason({
    slug: detailData.slug,
    title: detailData.title,
    rentPrice: detailData.rentPrice ?? null,
    gender: detailData.roomData?.gender ?? null,
    availableFrom: detailData.roomData?.availableFrom ?? null,
    petFriendly: detailData.roomData?.petFriendly ?? null,
    contract: detailData.roomData?.contract ?? null,
    smokingAllowed: detailData.roomData?.smokingAllowed ?? null
  }) : null;
  const handleRoomSelect = (slug: string) => {
    if (slug === detailData.slug) return;
    if (onRoomChange) {
      onRoomChange(slug);
      return;
    }
    if (isInModal) {
      handleOpenDialog(slug);
      return;
    }
    const parts = pathname.split("/");
    parts[parts.length - 1] = slug;
    // Preserve the query string (room filters) so the newly selected room is
    // still flagged against the active search.
    const query = searchParams?.toString();
    router.push(query ? `${parts.join("/")}?${query}` : parts.join("/"));
  };
  const heroThumbnails = isDemand ? user?.image ? [user.image] : [] : pictures;
  return orientation === "portrait" ? <RealEstateDetailMobileScaffold overlay={isSwitchingRoom && <RoomSwitchOverlay />} fab={!isOwner && user && <RealEstateDetailMobileMessageFab detailData={realEstateDetail.data?.data} aboveNavbar={!inDialog} />} onBack={handleGoBack} backLabel={tRealEstateDetail("back")} actions={<>
          <CompareGroupButton realEstateSlug={detailData.slug} />
          <ShareDrawer realEstateId={realEstateSlug} className={"w-5 h-5 text-foreground-heading"} title={detailData?.title} />
          <CardFavorite realEstateSlug={detailData?.slug} realEstateId={detailData?.id} favorite={isFavorite} className="text-transparent" />
        </>} hero={<RealEstateDetailImageSectionMobileInDetail thumbnail={heroThumbnails} classname="h-[45vh] w-full" hideCounter onCurrentChange={setHeroCurrent} />} heroBadge={heroThumbnails.length > 1 ? <div className="flex items-center justify-center rounded-md bg-gray-900/80 px-2.5 py-1">
            <p className="text-xs text-white">
              {heroCurrent}/{heroThumbnails.length}
            </p>
          </div> : undefined} data-sentry-element="RealEstateDetailMobileScaffold" data-sentry-component="RealEstateDetailMobile" data-sentry-source-file="RealEstateDetailMobile.tsx">
      <DetailHeaderBlock data-sentry-element="DetailHeaderBlock" data-sentry-source-file="RealEstateDetailMobile.tsx">
                <TitleSwitcher label={detailData?.portfolioData?.title ?? detailData?.title ?? ""} items={switcherItems} currentId={switcherCurrentId} multiline className={cn("font-semibold text-2xl capitalize text-foreground-heading", detailData?.portfolioData?.title && "text-primary")} chevronClassName="size-5" data-sentry-element="TitleSwitcher" data-sentry-source-file="RealEstateDetailMobile.tsx" />
                <div className="flex items-baseline gap-0.5">
                  <Price className="font-semibold text-md" businessType={detailData.businessType} salePrice={detailData.salePrice} lastBidPrice={detailData.lastBidPrice} minPrice={detailData.minPrice} rentPrice={detailData.rentPrice} data-sentry-element="Price" data-sentry-source-file="RealEstateDetailMobile.tsx" />
                  {rentLike && Number(detailData.rentPrice) > 0 && <span className="text-[11px] text-muted-foreground">
                      {tLandingPage("perMonth")}
                    </span>}
                </div>
                <p className="text-faint-foreground text-xs">
                  {analytics?.viewCount ?? 0} {tRealEstateDetail("views")} ·{" "}
                  {analytics?.uniqueViewCount ?? 0}{" "}
                  {tRealEstateDetail("visitors")} ·{" "}
                  {analytics?.favoritesCount ?? 0} {tRealEstateDetail("saved")}
                </p>
            </DetailHeaderBlock>
            {(() => {
      const areaValue = detailData.totalArea || detailData.usableArea || detailData.plotArea;
      const facts = [detailData.bedrooms && tPills("bedroomsCount", {
        count: detailData.bedrooms
      }), detailData.bathrooms && tPills("bathroomsCount", {
        count: detailData.bathrooms
      }), detailData.garage && tPills("garageCount", {
        count: detailData.garage
      }), areaValue && `${new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1
      }).format(Number(areaValue))} m²`, detailData?.roomData?.deposit && tPills("depositLabel", {
        value: `${detailData.roomData.deposit}€`
      })].filter(Boolean);
      if (facts.length === 0) return null;
      return <DetailFactsRow items={facts} />;
    })()}

            {roomRailItems.length > 1 && <div className="mt-4">
                <RoomRail rooms={roomRailItems} activeSlug={realEstateSlug} onSelect={handleRoomSelect} filterParams={detailFilters} className="-mx-2 px-2" />
              </div>}
            {currentRoomOutsideReason && <NotificationMessage variant="warning" hasCloseButton={false} className="mt-4" title={tRealEstateDetail("roomOutsideFiltersTitle")} description={currentRoomOutsideReason} />}

            {attributeRows.length > 0 && <DetailAttributeRows rows={attributeRows} />}

            {isDemand && (user?.name || user?.bio) && <div className="mt-4">
                <DemandUserCard user={user} hideAvatar />
              </div>}
            {hasRenderableDescription(detailData.description) ? <div className="mt-4 p-4">
                <h2 className="text-lg font-bold mb-3 text-foreground-heading">
                  {tRealEstateDetail("description")}
                </h2>
                <RealEstateDescriptionText description={detailData.description} />
              </div> : isSwitchingRoom ? <DetailDescriptionSkeleton /> : null}
            {detailData.roomData && <div className="mt-4">
                <RealEstateDetailHouseRules petFriendly={detailData.roomData.petFriendly} contract={detailData.roomData.contract} smokingAllowed={detailData.roomData.smokingAllowed} isDemand={isDemand} />
              </div>}
            {!isDemand && !!detailData.roomData?.bills && <div className="mt-4">
                <RealEstateDetailBills bills={detailData.roomData.bills} />
              </div>}
            {detailData.businessType !== BusinessType.RoomRent && !user && <div className="mt-6">
                <RealEstateAgencyCard title={detailData?.title} realEstateId={realEstateSlug} realEstateData={realEstateDetail.data?.data} />
              </div>}
            {detailData.businessType === BusinessType.RoomRent && !isOwner && user && <div className="mt-6">
                  <RealEstateAgencyCard title={detailData?.title} realEstateId={realEstateSlug} realEstateData={realEstateDetail.data?.data} />
                </div>}
            <LocationCard bare title={tRealEstateDetail("location")} locationName={isDemand ? <DemandLocationName locations={locations} /> : locationDisplay} lat={isDemand ? undefined : detailData?.lat} lng={isDemand ? undefined : detailData?.lng} polygon={isDemand ? locationPolygons : undefined} onMapClick={handleGoToMap} className="relative z-10 mt-4" mapClassName="h-56" data-sentry-element="LocationCard" data-sentry-source-file="RealEstateDetailMobile.tsx" />
            {featuresData && Array.isArray(featuresData) && featuresData.length > 0 && detailData.businessType !== BusinessType.Auction && <div className="mt-4 p-4">
                  {featurePills.hasAny ? <RoomFeaturePills room={featurePills.room} house={featurePills.house} /> : <RealEstateFeaturesCards features={featuresData} />}
                </div>}
            {!inDialog && detailData?.updatedAt && <p className="text-[11px] text-faint-foreground text-center mt-6">
                {tRealEstateDetail("updatedAgo", {
        time: formatTimeAgo(detailData.updatedAt, locale === "pt" ? "pt" : "enGB")
      })}
              </p>}
    </RealEstateDetailMobileScaffold> : <RealEstateDetailImageSectionMobileInDetail thumbnail={pictures} classname="h-full w-screen" data-sentry-element="RealEstateDetailImageSectionMobileInDetail" data-sentry-component="RealEstateDetailMobile" data-sentry-source-file="RealEstateDetailMobile.tsx" />;
}